/**
 * MOMAVO "For Her" Product Page — JavaScript
 * Gallery web component, message card expand, scroll reveals
 */

/* ---- Gallery Web Component ---- */
class MomavoForherGallery extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('.momavo-forher__gallery-list');
    this.dots = this.querySelectorAll('.momavo-forher__gallery-dot');
    this.items = this.querySelectorAll('.momavo-forher__gallery-item');

    if (!this.list || this.items.length === 0) return;

    this.#initCarousel();
    this.#initDesktopZoom();
  }

  disconnectedCallback() {
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
    }
  }

  #initCarousel() {
    if (this.dots.length === 0) return;

    for (const dot of this.dots) {
      dot.addEventListener('click', () => {
        const index = Number(dot.dataset.index);
        const target = this.items[index];
        if (target) {
          this.list.scrollTo({
            left: target.offsetLeft,
            behavior: 'smooth',
          });
        }
      });
    }

    this.scrollObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const index = Number(entry.target.dataset.index);
            this.#setActiveDot(index);
          }
        }
      },
      {
        root: this.list,
        threshold: 0.6,
      }
    );

    for (const item of this.items) {
      this.scrollObserver.observe(item);
    }
  }

  #setActiveDot(activeIndex) {
    for (const dot of this.dots) {
      const isActive = Number(dot.dataset.index) === activeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', String(isActive));
    }
  }

  #initDesktopZoom() {
    const mq = window.matchMedia('(min-width: 750px) and (hover: hover)');
    if (!mq.matches) return;

    for (const item of this.items) {
      const img = item.querySelector('img');
      if (!img) continue;

      item.addEventListener('mousemove', (e) => {
        const rect = item.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = `${x}% ${y}%`;
        img.style.transform = 'scale(1.6)';
      });

      item.addEventListener('mouseleave', () => {
        img.style.transformOrigin = 'center center';
        img.style.transform = 'scale(1)';
      });
    }
  }
}

if (!customElements.get('momavo-forher-gallery')) {
  customElements.define('momavo-forher-gallery', MomavoForherGallery);
}

/* ---- Variant Picker + Dynamic Price ---- */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('.momavo-forher__variant-btn');
  if (!btn) return;

  const group = btn.closest('.momavo-forher__variant-options');
  for (const b of group.querySelectorAll('.momavo-forher__variant-btn')) {
    b.classList.remove('is-selected');
  }
  btn.classList.add('is-selected');

  const section = btn.closest('.momavo-forher');
  if (!section) return;

  const variantsEl = section.querySelector('[data-momavo-variants]');
  if (!variantsEl) return;

  const variants = JSON.parse(variantsEl.textContent);
  const selectedOptions = [];
  for (const optGroup of section.querySelectorAll('.momavo-forher__variant-options')) {
    const sel = optGroup.querySelector('.momavo-forher__variant-btn.is-selected');
    if (sel) selectedOptions.push(sel.dataset.optionValue);
  }

  const match = variants.find((v) => {
    return v.options.every((opt, i) => opt === selectedOptions[i]);
  });

  if (!match) return;

  const formatMoney = (cents) => {
    const amt = (cents / 100).toFixed(2);
    return '$' + amt;
  };

  const priceEl = section.querySelector('[data-current-price]');
  const compareEl = section.querySelector('[data-compare-price]');
  const badgeEl = section.querySelector('[data-save-badge]');
  const priceArea = section.querySelector('[data-price-area]');
  const formInput = section.querySelector('input[name="id"]');
  const atcBtn = section.querySelector('.momavo-forher__atc-btn');

  if (priceEl) priceEl.textContent = formatMoney(match.price);

  if (match.compare_at_price > match.price) {
    if (compareEl) {
      compareEl.innerHTML = '<s>' + formatMoney(match.compare_at_price) + '</s>';
      compareEl.style.display = '';
    } else if (priceArea) {
      const newCompare = document.createElement('span');
      newCompare.className = 'momavo-forher__compare-price';
      newCompare.setAttribute('data-compare-price', '');
      newCompare.innerHTML = '<s>' + formatMoney(match.compare_at_price) + '</s>';
      priceArea.insertBefore(newCompare, priceEl);
    }
    const pct = Math.round(((match.compare_at_price - match.price) / match.compare_at_price) * 100);
    if (badgeEl) {
      badgeEl.textContent = 'Save ' + pct + '%';
      badgeEl.style.display = '';
    } else if (priceArea) {
      const newBadge = document.createElement('span');
      newBadge.className = 'momavo-forher__save-badge';
      newBadge.setAttribute('data-save-badge', '');
      newBadge.textContent = 'Save ' + pct + '%';
      priceArea.appendChild(newBadge);
    }
  } else {
    if (compareEl) compareEl.style.display = 'none';
    if (badgeEl) badgeEl.style.display = 'none';
  }

  if (formInput) formInput.value = match.id;

  if (atcBtn) {
    atcBtn.disabled = !match.available;
    const textNode = atcBtn.querySelector('.momavo-forher__atc-icon');
    const currentText = atcBtn.textContent.trim();
    if (!match.available && !currentText.includes('Sold Out')) {
      atcBtn.childNodes[atcBtn.childNodes.length - 1].textContent = ' Sold Out';
    } else if (match.available && currentText.includes('Sold Out')) {
      atcBtn.childNodes[atcBtn.childNodes.length - 1].textContent = ' Add to Cart';
    }
  }

  /* Sync sticky bar with variant change */
  const stickyBar = document.getElementById('MomavoStickyBar');
  if (stickyBar) {
    const stickyPrice = stickyBar.querySelector('[data-sticky-price]');
    if (stickyPrice) {
      let html = '';
      if (match.compare_at_price > match.price) {
        html += '<s class="momavo-forher__sticky-compare">' + formatMoney(match.compare_at_price) + '</s> ';
      }
      html += formatMoney(match.price);
      stickyPrice.innerHTML = html;
    }

    const stickySelect = stickyBar.querySelector('[data-sticky-option="' + btn.dataset.optionPosition + '"]');
    if (stickySelect) stickySelect.value = btn.dataset.optionValue;

    const stickyAtc = stickyBar.querySelector('[data-sticky-atc]');
    const stickyAtcText = stickyBar.querySelector('[data-sticky-atc-text]');
    if (stickyAtc) stickyAtc.disabled = !match.available;
    if (stickyAtcText) stickyAtcText.textContent = match.available ? 'Add to Cart' : 'Sold Out';
  }
});

/* ---- Message Card Expand/Collapse ---- */
document.addEventListener('click', (e) => {
  const toggle = e.target.closest('.momavo-forher__message-toggle');
  if (!toggle) return;
  const text = toggle.parentElement.querySelector('.momavo-forher__message-text');
  if (!text) return;
  const expanded = text.classList.toggle('is-expanded');
  toggle.textContent = expanded ? 'Show less' : 'Show full quote';
  toggle.setAttribute('aria-expanded', String(expanded));
});

/* ---- Scroll Reveal ---- */
document.addEventListener('DOMContentLoaded', () => {
  const reveals = document.querySelectorAll('.momavo-reveal');
  if (reveals.length === 0 || !('IntersectionObserver' in window)) return;

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      }
    },
    { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
  );

  for (const el of reveals) {
    observer.observe(el);
  }
});

/* ---- Sticky Add to Cart Bar ---- */
document.addEventListener('DOMContentLoaded', () => {
  const stickyBar = document.getElementById('MomavoStickyBar');
  const mainAtcBtn = document.querySelector('.momavo-forher__atc-btn');
  if (!stickyBar || !mainAtcBtn) return;

  /* Show/hide based on main ATC button visibility */
  const atcObserver = new IntersectionObserver(
    ([entry]) => {
      stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
      stickyBar.setAttribute('aria-hidden', String(entry.isIntersecting));
    },
    { threshold: 0 }
  );
  atcObserver.observe(mainAtcBtn);

  /* Sticky dropdown changes → update main variant buttons */
  const stickySelects = stickyBar.querySelectorAll('[data-sticky-option]');
  for (const select of stickySelects) {
    select.addEventListener('change', () => {
      const position = select.dataset.stickyOption;
      const value = select.value;
      const mainBtn = document.querySelector(
        '.momavo-forher__variant-btn[data-option-position="' + position + '"][data-option-value="' + CSS.escape(value) + '"]'
      );
      if (mainBtn) mainBtn.click();
    });
  }

  /* Sticky ATC → submit the main product form */
  const stickyAtcBtn = stickyBar.querySelector('[data-sticky-atc]');
  if (stickyAtcBtn) {
    stickyAtcBtn.addEventListener('click', () => {
      if (stickyAtcBtn.disabled) return;
      const mainForm = document.getElementById('MomavoForherForm');
      if (mainForm) {
        mainForm.requestSubmit();
      }
    });
  }
});
